// Version Check Utility - Simple localStorage-based version checking
// This utility checks for version updates by comparing with stored version
import { GRAPHQL_URL } from '../config';

class VersionChecker {
  constructor() {
    // Will be updated from server version.json
    this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
    this.storageKey = 'app_version';
    this.isCheckingVersion = false;
    this.hasShownUpdateNotice = false;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  // Initialize version checking - checks on page load, focus, and visibility changes
  startVersionCheck() {
    // Make globally available for manual testing
    if (typeof window !== 'undefined') {
      window.versionChecker = this;
    }
    
    // Update current version from server
    this.updateCurrentVersionFromServer();
    
    // Check if we just completed an update.
    // Do NOT overwrite app_version with the build-time REACT_APP_VERSION — cache bust
    // bumps the DB/server version while the JS bundle version stays the same.
    try {
      const isUpdating = localStorage.getItem('app_updating');
      if (isUpdating === 'true') {
        localStorage.removeItem('app_updating');
        this.hasShownUpdateNotice = true;
        const acknowledgedVersion = localStorage.getItem(this.storageKey);
        if (acknowledgedVersion) {
          this.currentVersion = acknowledgedVersion;
        }
      }
    } catch (e) {
      // Silent cleanup
    }

    // IMPORTANT: Do an initial check on startup
    setTimeout(() => {
      if (!this.isDisabled()) {
        this.checkForUpdates();
      }
    }, 3000); // Slightly longer delay to let app fully initialize

    // Add event listeners for page focus/visibility changes
    this.handleVisibilityChange = () => {
      if (!document.hidden && !this.isDisabled()) {
        // Check for updates when user returns to the app
        setTimeout(() => {
          this.checkForUpdates();
        }, 1000); // Small delay to ensure app is ready
      }
    };

    this.handleFocus = () => {
      if (!this.isDisabled()) {
        // Check for updates when user focuses the app
        setTimeout(() => {
          this.checkForUpdates();
        }, 1000);
      }
    };

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('focus', this.handleFocus);
  }

  // Update current version from server version.json
  async updateCurrentVersionFromServer() {
    try {
      const serverVersionData = await this.fetchServerVersion();
      if (serverVersionData && serverVersionData.version) {
        this.currentVersion = serverVersionData.version;
      }
    } catch (error) {
      // Silent fail - keep using build-time version
    }
  }

  // Stop version checking - now just cleanup
  stopVersionCheck() {
    // Remove any event listeners if they exist
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);
  }

  // Check for updates by comparing with stored version
  async checkForUpdates() {
    if (this.isCheckingVersion) {
      return;
    }

    // Check if version checking is permanently disabled
    const isDisabled = localStorage.getItem('version_check_disabled');
    if (isDisabled === 'true') {
      return;
    }

    this.isCheckingVersion = true;

    try {
      // Get the stored version from localStorage
      const storedVersion = localStorage.getItem(this.storageKey);
      
      // Fetch the current version from the server
      const serverVersionData = await this.fetchServerVersion();
      
      if (serverVersionData && serverVersionData.version) {
        const serverVersion = serverVersionData.version;
        
        // Update our current version to match server
        this.currentVersion = serverVersion;
        
        // If no stored version, this is the first run - store server version
        if (!storedVersion) {
          localStorage.setItem(this.storageKey, serverVersion);
        } else if (this.isVersionDifferent(storedVersion, serverVersion)) {
          // Check if we've already shown update notice for this specific version
          const lastShownVersion = localStorage.getItem('last_shown_update_version');
          if (lastShownVersion !== serverVersion) {
            // Store the version to prevent repeated notifications
            localStorage.setItem('last_shown_update_version', serverVersion);
            // Don't show popup - let the slide-out notification handle it
          }
        }
      }
    } catch (error) {
      // Silent error handling
    } finally {
      this.isCheckingVersion = false;
    }
  }

  // Compare versions to see if they're different
  isVersionDifferent(storedVersion, serverVersion) {
    return storedVersion !== serverVersion;
  }

  // Handle version update by showing notification
  async handleVersionUpdate(serverVersion) {
    try {
      // Store the new version to prevent repeated popups for the same version
      localStorage.setItem(this.storageKey, serverVersion);
      localStorage.setItem('last_shown_update_version', serverVersion);
      
      // Show update notification with changelog
      this.showUpdateNotification(serverVersion);
    } catch (error) {
      // If anything fails, just force reload
      this.forceReload();
    }
  }

  // Show update notification modal with changelog
  async showUpdateNotification(serverVersion) {
    try {
      // Fetch changelog data
      const changelogData = await this.fetchChangelogData();
      const formattedChangelog = this.formatChangelogForDisplay(changelogData);
      
      // Create and show the update notification modal
      const modal = this.createUpdateNotification(serverVersion, formattedChangelog);
      document.body.appendChild(modal);
    } catch (error) {
      // If changelog fails, show simple notification
      const modal = this.createUpdateNotification(serverVersion, null);
      document.body.appendChild(modal);
    }
  }

  // Create update notification modal
  createUpdateNotification(serverVersion, changelog) {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.id = 'update-notification-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      font-family: system-ui, -apple-system, sans-serif;
      animation: fadeIn 0.3s ease-in-out;
    `;

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideIn {
        from { transform: translateY(-20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);

    // Create modal content
    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 16px;
      padding: 2.5rem;
      max-width: 95vw;
      max-height: 85vh;
      overflow-y: auto;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      margin: 1rem;
      animation: slideIn 0.3s ease-in-out;
      border: 2px solid #3b82f6;
    `;

    const changelogSection = changelog ? `
      <div style="margin: 1.5rem 0; padding: 1.5rem; background: #f0f9ff; border-radius: 12px; border-left: 4px solid #3b82f6;">
        <h3 style="margin: 0 0 1rem; color: #1e40af; font-size: 1.1rem; font-weight: 600;">📋 What's New:</h3>
        <div style="color: #475569; font-size: 0.95rem; line-height: 1.6;">${changelog}</div>
      </div>
    ` : '';

    content.innerHTML = `
      <div style="text-align: center;">
        <div style="font-size: 4rem; margin-bottom: 1rem;">🚀</div>
        <h2 style="margin: 0 0 1rem; color: #1f2937; font-size: 1.8rem; font-weight: 700;">
          Update Required
        </h2>
        <p style="margin: 0 0 0.75rem; color: #4b5563; font-size: 1.15rem; font-weight: 500;">
          Version ${serverVersion} is ready. Clicking Update is mandatory to continue using the app.
        </p>
        <p style="margin: 0 0 1.5rem; color: #6b7280; font-size: 1rem;">
          The update is instant — your browser will refresh and you will immediately have the newest version.
        </p>
        
        ${changelogSection}
        
        <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 1rem; margin: 1.5rem 0;">
          <p style="margin: 0; color: #92400e; font-size: 0.9rem; font-weight: 500;">
            ⚠️ You cannot dismiss this dialog. Please update now to continue.
          </p>
        </div>
        
        <div style="display: flex; gap: 1rem; justify-content: center; margin-top: 2rem;">
          <button id="reload-now-btn" style="
            background: linear-gradient(135deg, #10b981, #059669);
            color: white;
            border: none;
            padding: 1rem 2.5rem;
            border-radius: 12px;
            cursor: pointer;
            font-weight: 700;
            font-size: 1.1rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            transition: all 0.2s;
            min-width: 200px;
          " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
            ✅ Update Now
          </button>
        </div>
      </div>
    `;

    modal.appendChild(content);

    // Function to clean up modal
    const cleanupModal = () => {
      if (document.body.contains(modal)) {
        document.body.removeChild(modal);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };

    // Add event listeners
    const reloadNowBtn = modal.querySelector('#reload-now-btn');

    reloadNowBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Show loading state
      reloadNowBtn.innerHTML = '⏳ Updating...';
      reloadNowBtn.style.background = '#6b7280';
      reloadNowBtn.disabled = true;
      
      // Clean up and reload
      setTimeout(() => {
        cleanupModal();
        this.forceReload();
      }, 500);
    });

    return modal;
  }

  // Fetch server version from GraphQL API (more reliable than version.json)
  async fetchServerVersion() {
    try {
      const query = `
        query GetPublicVersion {
          getPublicVersion {
            version
            buildTime
            lastUpdated
          }
        }
      `;

      const graphqlUrl = GRAPHQL_URL;
      
      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.data?.getPublicVersion) {
          return result.data.getPublicVersion;
        }
      }
      
      throw new Error(`GraphQL request failed: ${response.status}`);
    } catch (error) {
      // Fallback: try version.json if GraphQL fails
      try {
        const timestamp = new Date().getTime();
        const response = await fetch(`/version.json?_cb=${timestamp}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (response.ok) {
          return await response.json();
        }
      } catch (fallbackError) {
        // Silent fallback
      }
      
      return null;
    }
  }

  // Helper to get cookie value
  getCookieValue(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  // Fetch changelog data
  async fetchChangelogData() {
    try {
      const response = await fetch(`/changelog.json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        return null;
      }
      
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Format changelog for display
  formatChangelogForDisplay(changelogData) {
    if (!changelogData || !changelogData.entries || !Array.isArray(changelogData.entries)) {
      return null;
    }

    // Get the latest entry
    const latestEntry = changelogData.entries[0];
    if (!latestEntry) {
      return null;
    }

    let formatted = '';
    
    if (latestEntry.changes && Array.isArray(latestEntry.changes)) {
      formatted = latestEntry.changes
        .map(change => `• ${change}`)
        .join('<br>');
    }

    return formatted || null;
  }

  // Force reload with cache busting (PWA-optimized)
  // Pass acknowledgedVersion so the update modal does not reappear after reload.
  async forceReload(acknowledgedVersion = null) {
    try {
      if (acknowledgedVersion) {
        localStorage.setItem(this.storageKey, acknowledgedVersion);
        this.currentVersion = acknowledgedVersion;
      }
      localStorage.setItem('app_updating', 'true');
      localStorage.setItem('app_last_reload', new Date().toISOString());
      
      // Clear session storage
      sessionStorage.clear();
      
      // PWA: Send message to service worker to force cache clear
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        try {
          navigator.serviceWorker.controller.postMessage({
            type: 'FORCE_CACHE_CLEAR'
          });
        } catch (error) {
          // Silent error handling
        }
      }
      
      // PWA: Unregister service worker to force fresh installation
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(
            registrations.map(registration => {
              return registration.unregister();
            })
          );
        } catch (error) {
          // Silent error handling
        }
      }
      
      // Clear ALL caches and wait for completion
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            return caches.delete(cacheName);
          })
        );
      }
      
      // Add cache busting parameter to URL
      const url = new URL(window.location.href);
      url.searchParams.set('_v', Date.now());
      url.searchParams.set('_bust', 'true');
      
      // Force hard reload with cache busting URL
      window.location.href = url.href;
    } catch (error) {
      // Fallback to simple reload with cache busting
      const url = new URL(window.location.href);
      url.searchParams.set('_v', Date.now());
      window.location.href = url.href;
    }
  }

  // Manual check method (for admin use)
  async checkNow() {
    await this.checkForUpdates();
  }

  // Force check even in development (for testing)
  async forceCheck() {
    const originalDev = this.isDevelopment;
    this.isDevelopment = false;
    await this.checkForUpdates();
    this.isDevelopment = originalDev;
  }

  // Test version update (for development)
  async testVersionUpdate() {
    this.hasShownUpdateNotice = false;
    await this.handleVersionUpdate('test-version-' + Date.now());
  }

  // Permanently disable version checking
  disableVersionChecking() {
    localStorage.setItem('version_check_disabled', 'true');
    this.hasShownUpdateNotice = true;
  }

  // Re-enable version checking
  enableVersionChecking() {
    localStorage.removeItem('version_check_disabled');
    this.hasShownUpdateNotice = false;
  }

  // Check if version checking is disabled
  isDisabled() {
    return localStorage.getItem('version_check_disabled') === 'true';
  }

  // Reset version checker state (for debugging)
  reset() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem('app_updating');
    localStorage.removeItem('app_last_reload');
    localStorage.removeItem('version_check_disabled');
    localStorage.removeItem('last_shown_update_version');
    this.hasShownUpdateNotice = false;
  }

  // Clear stored version to force update check (for testing)
  clearStoredVersion() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem('last_shown_update_version');
    this.hasShownUpdateNotice = false;
  }
  
  // Test the update notification system (for debugging)
  async testUpdateNotification() {
    const testVersion = 'test-' + Date.now();
    
    // Don't store the test version in localStorage to avoid confusion
    const originalStorageKey = this.storageKey;
    this.storageKey = 'test_app_version'; // Use temporary key
    
    await this.handleVersionUpdate(testVersion);
    
    // Restore original key and clean up test data
    this.storageKey = originalStorageKey;
    localStorage.removeItem('test_app_version');
    localStorage.removeItem('last_shown_update_version');
  }

  // Force a version check (for debugging)
  async forceVersionCheck() {
    this.isCheckingVersion = false; // Reset the flag
    await this.checkForUpdates();
  }

  // Clear all version data and force fresh check (for debugging)
  clearAllVersionData() {
    localStorage.removeItem(this.storageKey);
    localStorage.removeItem('last_shown_update_version');
    localStorage.removeItem('app_updating');
    localStorage.removeItem('app_last_reload');
    this.hasShownUpdateNotice = false;
  }
}

// Create and export singleton instance
const versionChecker = new VersionChecker();
export default versionChecker; 